import { Box, Button, ButtonProps, Stack, Typography } from '@mui/material';
import { CENTERED_ICON, COLORS, MOTION, RADIUS } from '../../constants';
import { MaterialIcon } from '../materialIcon';

type Brand = 'visa' | 'mastercard' | 'amex' | 'other';

const getBrand = (issuer = ''): Brand => {
  const value = issuer.toLowerCase();
  if (value.includes('visa')) return 'visa';
  if (value.includes('master')) return 'mastercard';
  if (value.includes('amex') || value.includes('american')) return 'amex';
  return 'other';
};

const badgeSx = {
  flexShrink: 0,
  width: 42,
  height: 28,
  borderRadius: '4px',
  border: `1px solid ${COLORS.text.light4}`,
  // Card network logos are designed for white
  backgroundColor: '#FFFFFF',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
};

/**
 * Small card network badge drawn with CSS, same as customer-mobile's CardBrand.
 *
 * @param {string} [issuer] - The card issuer, e.g. "mastercard debit".
 * @return {React.JSX.Element} The rendered badge.
 */
export const CardBrand = ({ issuer }: { issuer?: string }) => {
  switch (getBrand(issuer)) {
    case 'visa':
      return (
        <Box aria-hidden sx={{ ...badgeSx, fontStyle: 'italic', fontSize: 12, color: '#1A1F71' }}>
          VISA
        </Box>
      );
    case 'mastercard':
      return (
        <Box aria-hidden sx={badgeSx}>
          <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#EB001B' }} />
          <Box
            sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#F79E1B', ml: '-6px', opacity: 0.9 }}
          />
        </Box>
      );
    case 'amex':
      return (
        <Box
          aria-hidden
          sx={{ ...badgeSx, fontSize: 10, color: '#FFFFFF', backgroundColor: '#2E77BC', borderColor: '#2E77BC' }}
        >
          AMEX
        </Box>
      );
    default:
      return (
        <Box aria-hidden sx={{ ...badgeSx, fontSize: 8, color: COLORS.text.light, overflow: 'hidden' }}>
          {issuer?.toUpperCase().slice(0, 6) || 'CARD'}
        </Box>
      );
  }
};

/**
 * Badge-sized tile with a cash icon, same footprint as the card network badge.
 *
 * @return {React.JSX.Element} The rendered badge.
 */
const CashBadge = () => (
  <Box aria-hidden sx={badgeSx}>
    <MaterialIcon icon="payments" size={18} sx={{ ...CENTERED_ICON, color: COLORS.text.brand }} />
  </Box>
);

/**
 * Renders a payment option card like customer-mobile's OptionCard: white, subtle outline, brand badge for cards
 * and a cash badge for the cash gateway.
 *
 * @param {'cash' | 'card'} gateway - The payment gateway type.
 * @param {string} title - The title of the payment selection, e.g. "Débito" or "Efectivo".
 * @param {string} [description] - The optional description shown under the title.
 * @param {string} [issuer] - The card issuer, shown as a network badge.
 * @param {string} [cardNumber] - The card number, shown masked with its last 4 digits.
 * @param {ButtonProps} buttonProps - Additional props for the Button component.
 * @return {React.JSX.Element} The rendered payment selection button.
 */
export function PaymentSelection({
  gateway,
  title,
  description,
  issuer,
  cardNumber,
  ...buttonProps
}: {
  gateway: 'cash' | 'card';
  title: string;
  description?: string;
  issuer?: string;
  cardNumber?: string;
} & ButtonProps) {
  const lastDigits = cardNumber?.slice(-4);
  return (
    <Button
      type="submit"
      data-testid={`gateway-${gateway}`}
      aria-label={lastDigits ? `${title} terminada en ${lastDigits}` : undefined}
      sx={{
        gap: 1.5,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-start',
        alignItems: 'center',
        width: '100%',
        minHeight: 'auto',
        textAlign: 'left',
        border: `1px solid ${COLORS.border.subtle}`,
        borderRadius: `${RADIUS.lg}px`,
        paddingBlock: 2,
        paddingInline: 2.5,
        color: COLORS.text.main,
        backgroundColor: COLORS.surface.raised,
        '&:hover, &:focus-visible': {
          backgroundColor: COLORS.surface.raised,
          borderColor: COLORS.brand.main,
        },
        '&:active': { transform: `scale(${MOTION.cardPressScale})` },
        transition: `transform ${MOTION.fast}ms ${MOTION.easeOut}, border-color ${MOTION.short}ms`,
      }}
      {...buttonProps}
    >
      {issuer && <CardBrand issuer={issuer} />}
      {gateway === 'cash' && <CashBadge />}
      <Stack direction="column" sx={{ flex: 1, minWidth: 0, alignItems: 'flex-start', gap: 0.5 }}>
        <Typography component="span" variant="body1" sx={{ fontWeight: 500, lineHeight: '1.375rem' }}>
          {title}
        </Typography>
        {description && (
          <Typography component="span" variant="subtitle2">
            {description}
          </Typography>
        )}
      </Stack>
      {lastDigits && (
        <Typography
          component="span"
          variant="body1"
          sx={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}
        >
          •••• •••• •••• {lastDigits}
        </Typography>
      )}
    </Button>
  );
}
